<template>
  <article class="public-username-editor">
    <section v-if="randomWordsWithColors.length > 0" class="word-picker">
      <ul class="word-options">
        <li
          v-for="{ word, color } in randomWordsWithColors"
          :key="word['id']"
          :data-word-id="word['id']"
          class="word-option"
          :class="[color]"
          draggable="true"
          @click="onClickWord"
          @dragstart="onDragWord"
        >
          <span class="word">{{ word['word'] }}</span>
        </li>
      </ul>
    </section>

    <ErrorMessage v-if="hasErrors">
      <p>
        {{ $t('adaptive-test-battle.unable-to-load-words-error-message') }}
      </p>
    </ErrorMessage>

    <section class="picked-words">
      <div class="drop-zone-wrapper">
        <button
          class="reset-word-button"
          :title="$t('adaptive-test-battle.reset-word-button')"
          @click="selectedWordOne = wordOneData"
        >
          <FaIcon icon="times" />
        </button>

        <div
          @dragstart="onDragWord"
          @dragenter="onDragOverWord"
          @dragover="onDragOverWord"
          @drop="onDropWord"
          draggable="true"
          class="picked-word word-one"
          :class="classObject(0)"
          :data-word-id="selectedWordOne ? selectedWordOne['id'] : 0"
          ref="wordOne"
        >
          <span class="word">{{ selectedWordOne['word'] }}</span>
        </div>
      </div>

      <div class="drop-zone-wrapper">
        <button
          class="reset-word-button"
          :title="$t('adaptive-test-battle.reset-word-button')"
          @click="selectedWordTwo = wordTwoData"
        >
          <FaIcon icon="times" />
        </button>

        <div
          @dragstart="onDragWord"
          @dragenter="onDragOverWord"
          @dragover="onDragOverWord"
          @drop="onDropWord"
          draggable="true"
          class="picked-word word-two"
          :class="classObject(1)"
          :data-word-id="selectedWordTwo ? selectedWordTwo['id'] : 0"
          ref="wordTwo"
        >
          <span class="word">{{ selectedWordTwo['word'] }}</span>
        </div>
      </div>
    </section>
  </article>
</template>

<script>
import UserApi from '../../../apis/user'
import { shuffle } from '../../../utils/array'
import FaIcon from '../../ui/FaIcon'
import ErrorMessage from '../../ui/ErrorMessage'

export default {
  name: 'PublicUsernameEditor',
  components: { ErrorMessage, FaIcon },
  props: {
    wordOneData: Object,
    wordTwoData: Object,
  },
  data() {
    return {
      isLoading: false,
      hasErrors: false,
      randomWords: [],
      allWordOptions: [],
      selectedWordOne: this.wordOneData,
      selectedWordTwo: this.wordTwoData,
      pickWordByClickActivated: false,
      indexOfwordToSet: 0,
      maxWordsToSet: 2,
      backgroundColors: ['indigo', 'lilac', 'violet'],
    }
  },
  mounted() {
    this.getWordOptions()
  },
  computed: {
    /**
     * Mapping random colors onto each random word for varied colorness!
     * @returns {Object} an object with classes and their conditions
     */
    randomWordsWithColors() {
      return this.randomWords.map(word => {
        return {
          word,
          color: this.getBackgroundColor(),
        }
      })
    },
  },
  methods: {
    /**
     * Applies the class "im-it", if the element in question is the next one to be picked by the mouse-click-picker.
     * Also applies a class to tell css that the element was activated by a click and not drag-n-drop.
     * @param {number} indexOfwordToSet
     * @returns {Object}
     * @private
     */
    classObject(indexOfwordToSet) {
      return {
        'im-it':
          this.pickWordByClickActivated &&
          this.indexOfwordToSet === indexOfwordToSet,
        clickActivated: this.pickWordByClickActivated,
      }
    },
    /**
     * Randomly chooses a color name from the array to be used as background color for each word option.
     * @returns {string}
     * @private
     */
    getBackgroundColor() {
      return this.backgroundColors[Math.floor(Math.random() * 3)]
    },
    /**
     * @private
     */
    onDragWord(event) {
      this.pickWordByClickActivated = false
      event.dataTransfer.setData('text/plain', this.getWordId(event.target))
    },
    /**
     * @private
     */
    onDragOverWord(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    /**
     * @private
     */
    onDropWord(event) {
      event.preventDefault()

      let pickedWordData = this.getPickedWordData(
        event.dataTransfer.getData('text/plain')
      )

      let target = event.target

      if (target == this.$refs.wordOne) {
        this.selectedWordOne = pickedWordData
      } else if (target == this.$refs.wordTwo) {
        this.selectedWordTwo = pickedWordData
      }

      this.bloopElement(target)
    },
    /**
     * Applies a bloop-like animation to the given element by adding a class and removing it soon after.
     * @param {Element} elementToBloop
     * @private
     */
    bloopElement(elementToBloop) {
      this.$nextTick(() => {
        elementToBloop.classList.add('bloop-animation')
      })
      setTimeout(() => {
        elementToBloop.classList.remove('bloop-animation')
      }, 200)
    },
    /**
     * @param {number|string} id
     * @returns {Object}
     * @private
     */
    getPickedWordData(id) {
      return this.allWordOptions.find(wordObject => {
        return wordObject['id'] == id
      })
    },
    /**
     * Picks a word by alternating between which existing word will be picked next when clicked,
     * and applies a bloop animation to the word that was just altered
     * @param {Object} event
     * @private
     */
    onClickWord(event) {
      this.pickWordByClickActivated = true
      let id = event.target.dataset.wordId
      let wordData = this.getPickedWordData(id)

      /**
       * @type {Element}
       */
      let elementToBloop = null

      if (this.indexOfwordToSet === 0) {
        this.selectedWordOne = wordData
        elementToBloop = this.$refs.wordOne
      } else {
        this.selectedWordTwo = wordData
        elementToBloop = this.$refs.wordTwo
      }

      this.indexOfwordToSet = (this.indexOfwordToSet + 1) % this.maxWordsToSet
      this.bloopElement(elementToBloop)
    },

    /**
     * Gets the words that a user can apply to their username and sets them in an array
     * @private
     */
    getWordOptions() {
      if (this.randomWords.length > 0) {
        return
      }

      this.isLoading = true

      new UserApi().getRandomUsernameWords().then(data => {
        if (data) {
          this.allWordOptions = data
          this.populatedAndShuffleWordOptions()
        } else {
          this.hasErrors = true
        }

        this.isLoading = false
      })
    },
    /**
     * @private
     */
    populatedAndShuffleWordOptions() {
      this.randomWords = shuffle(this.allWordOptions).slice(0, 9)
    },

    /**
     * @param {Element} wordElement
     * @returns {number}
     * @private
     */
    getWordId(wordElement) {
      return wordElement.getAttribute('data-word-id')
    },
    /**
     * @returns {{wordTwo: {word: string, id: number}, wordOne: {word: string, id: number}}}
     * @public
     */
    getEditedWords() {
      return {
        wordOne: this.selectedWordOne,
        wordTwo: this.selectedWordTwo,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.picked-word,
.word-option {
  &[draggable='true'] {
    cursor: grab;

    &[aria-grabbed] {
      cursor: grabbing;
    }
  }
}
article.public-username-editor {
  padding: 1em;
  background-color: $white;
  border-radius: $mainBorderRadius;
}

.picked-words {
  display: flex;
  margin: 2em 0;

  .drop-zone-wrapper {
    position: relative;
    flex: 1;
    flex-shrink: 0;
    margin: 0 0.5em;
    float: left;

    .reset-word-button {
      position: absolute;
      right: 0;
      font-size: 0.7em;
      background-color: $white;
      border-color: $red;
      color: $red;
      transform: translate(50%, -50%);
      border-radius: 2em;
      z-index: 1;
    }
  }

  .picked-word {
    padding: 1em 0.5em;
    border-radius: calc(#{$mainBorderRadius} * 2);
    border: 0.2em dashed $blueDark;
    transition: all 0.2s ease-in-out;

    .word {
      font-size: 1.2em;
      font-weight: bold;
      pointer-events: none;
    }

    &.bloop-animation {
      transform: scale(1.1);
    }
    &.clickActivated {
      &.im-it {
        opacity: 0.7;
      }
    }
  }
}

.word-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 1.5em;
  padding: 0;
  list-style: none;
  cursor: pointer;

  .word-option {
    --background-color: $indigo;
    display: inline-block;
    background-image: none;
    background: var(--background-color, $indigo);
    border-radius: $mainBorderRadius;
    text-align: center;
    color: $white;
    padding: 1em 0.5em;
    line-height: normal;

    .word {
      pointer-events: none;
    }

    &.lilac {
      background: $lilac;
    }
    &.violet {
      background: $violet;
    }
    &.indigo {
      background: $indigo;
    }
  }
}
</style>
